import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BadgeEuro,
  Building2,
  CircleAlert,
  Leaf,
  Loader2,
  Share2,
  Sparkles,
} from 'lucide-react';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { NewBuildVsOldBuildReport2026 } from '../../types/newBuildVsOldBuild2026';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatPct = (value: number) => `${((value || 0) * 100).toFixed(1)}%`;

const formatRate = (value: number) => `${((value || 0) * 100).toFixed(2)}%`;

const NewVsOldDetailPage: React.FC = () => {
  const { comparisonSlug } = useParams<{ comparisonSlug: string }>();
  const [data, setData] = useState<NewBuildVsOldBuildReport2026 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const slug = useMemo(() => String(comparisonSlug || '').trim().toLowerCase(), [comparisonSlug]);

  const mergedFaq = useMemo(() => {
    const allFaq = [...(data?.seo_content?.faq || []), ...(data?.ai_enrichment?.faq_additions || [])];
    const seen = new Set<string>();
    return allFaq.filter((item) => {
      const key = `${item.question.trim().toLowerCase()}|${item.answer.trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data]);

  useEffect(() => {
    if (!slug) {
      setError('Comparison slug is missing.');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getNewBuildVsOldBuildReport?slug=${encodeURIComponent(slug)}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError('Comparison report not found.');
          return;
        }
        setData(result.data as NewBuildVsOldBuildReport2026);
      } catch (e) {
        setError('Unable to load comparison report.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
        <p className="font-bold text-slate-500">Loading new-vs-old comparison...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-3">Comparison Unavailable</h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Link
            to="/new-vs-old"
            className="inline-block rounded-2xl bg-emerald-600 text-white px-8 py-4 font-black hover:bg-emerald-700 no-underline"
          >
            Open New vs Old Hub
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/new-vs-old/${data.slug}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText = `New build vs period property in ${data.county_name}: compare 5-year cost, BER heating gap and grants on RentSmart.ie.`;

  const comparisonRows = [
    {
      feature: 'Median Price',
      newBuild: formatCurrency(data.calculations.median_price_new_build),
      oldBuild: formatCurrency(data.calculations.median_price_second_hand),
    },
    {
      feature: 'Gov Grants',
      newBuild: `HTB (${formatCurrency(data.calculations.help_to_buy_max)} max) + First Home`,
      oldBuild: `SEAI Retrofit (${formatCurrency(data.calculations.seai_retrofit_grant_max)} max)`,
    },
    {
      feature: 'Typical BER',
      newBuild: 'A2 / A3',
      oldBuild: 'D1 to G',
    },
    {
      feature: 'Mortgage Rate',
      newBuild: `Green rate (${formatRate(data.calculations.green_mortgage_rate)})`,
      oldBuild: `Standard rate (${formatRate(data.calculations.standard_mortgage_rate)})`,
    },
    {
      feature: 'Renovation Required',
      newBuild: formatCurrency(0),
      oldBuild: formatCurrency(data.calculations.avg_renovation_cost),
    },
    {
      feature: 'Annual Heating',
      newBuild: formatCurrency(data.calculations.annual_heating_cost_new_build),
      oldBuild: formatCurrency(data.calculations.annual_heating_cost_second_hand),
    },
    {
      feature: '5-Year Total Cost',
      newBuild: formatCurrency(data.calculations.five_year_total_cost_new_build),
      oldBuild: formatCurrency(data.calculations.five_year_total_cost_second_hand),
    },
  ];

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>{data.seo_content.meta_title}</title>
        <meta name="description" content={data.seo_content.meta_desc} />
        <meta name="keywords" content={data.seo_content.keywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(data.schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-10 pb-20 px-4 relative overflow-hidden">
        <div className="absolute -top-20 -right-12 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-4">
                <Building2 className="h-4 w-4" /> New Build vs Old Build
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-200 mb-4">
                <AlertTriangle className="h-3.5 w-3.5" /> Planning Estimate Only
              </div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-3">{data.seo_content.h1}</h1>
              <p className="text-slate-300 text-lg leading-relaxed max-w-3xl mb-5">
                {data.seo_content.snippet_hook}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-black uppercase tracking-widest text-slate-400">Share:</span>
                <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                  <FacebookIcon size={30} round />
                </FacebookShareButton>
                <TwitterShareButton url={shareUrl} title={shareText}>
                  <TwitterIcon size={30} round />
                </TwitterShareButton>
                <WhatsappShareButton url={shareUrl} title={shareText}>
                  <WhatsappIcon size={30} round />
                </WhatsappShareButton>
                <LinkedinShareButton url={shareUrl} title="New Build vs Old Build" summary={shareText}>
                  <LinkedinIcon size={30} round />
                </LinkedinShareButton>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="rounded-[2rem] border border-white/20 bg-white/10 backdrop-blur p-6">
                <p className="text-[10px] uppercase tracking-widest font-black text-slate-300 mb-1">
                  5-Year Cost Delta (New Build)
                </p>
                <p className="text-4xl font-black text-emerald-300 mb-2">
                  {formatCurrency(data.calculations.five_year_total_saving_new_build)}
                </p>
                <p className="text-xs text-slate-200 font-semibold mb-4">
                  Positive values indicate lower modeled 5-year ownership cost for new builds.
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-300 mb-1">
                      Green Rate
                    </p>
                    <p className="font-black text-white">{formatRate(data.calculations.green_mortgage_rate)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-300 mb-1">
                      Heating Gap
                    </p>
                    <p className="font-black text-white">{formatCurrency(data.calculations.annual_heating_saving)}/yr</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 -mt-10 pb-20">
        <div className="grid lg:grid-cols-12 gap-8 mt-16">
          <section className="lg:col-span-8 space-y-8">
            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-9 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">Financial Snapshot</h2>
              <p className="text-sm text-slate-600 font-semibold leading-relaxed mb-5">{data.sections.snapshot_intro}</p>

              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      <th className="text-left px-4 py-3 font-black uppercase tracking-wider text-[10px]">Feature</th>
                      <th className="text-left px-4 py-3 font-black uppercase tracking-wider text-[10px]">New Build</th>
                      <th className="text-left px-4 py-3 font-black uppercase tracking-wider text-[10px]">Second-Hand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map((row, index) => (
                      <tr key={row.feature} className={index % 2 ? 'bg-slate-50' : 'bg-white'}>
                        <td className="px-4 py-3 font-black text-slate-900">{row.feature}</td>
                        <td className="px-4 py-3 font-semibold text-emerald-800">{row.newBuild}</td>
                        <td className="px-4 py-3 font-semibold text-slate-700">{row.oldBuild}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-9 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">Renovation Reality</h2>
              <p className="text-sm text-slate-700 font-semibold leading-relaxed mb-4">{data.sections.renovation_reality}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-amber-700 mb-1">Avg Retrofit Cost</p>
                  <p className="text-2xl font-black text-amber-900">
                    {formatCurrency(data.calculations.avg_renovation_cost)}
                  </p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700 mb-1">After SEAI Grants</p>
                  <p className="text-2xl font-black text-emerald-900">
                    {formatCurrency(data.calculations.estimated_retrofit_cost_after_grants)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-9 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">Location vs Space Trade-Off</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <h3 className="text-sm font-black text-slate-900 mb-2">Old Build Advantage</h3>
                  <ul className="space-y-2 text-sm text-slate-700 font-semibold">
                    {data.sections.location_vs_space.old_build_advantage.map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                  <h3 className="text-sm font-black text-emerald-900 mb-2">New Build Advantage</h3>
                  <ul className="space-y-2 text-sm text-emerald-900 font-semibold">
                    {data.sections.location_vs_space.new_build_advantage.map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-9 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">Local Development Spotlight</h2>
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                {data.sections.local_development_spotlight.map((name) => (
                  <div key={name} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-black text-slate-800">{name}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-700 font-semibold leading-relaxed mb-3">
                {data.sections.price_register_trend_text}
              </p>
              <p className="text-sm text-slate-700 font-semibold leading-relaxed">
                {data.sections.vacant_property_bonus}
              </p>
            </div>

            {data.ai_enrichment ? (
              <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-9 shadow-sm">
                <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" /> Buyer Execution Playbook
                </h2>
                {data.ai_enrichment.county_strategy_summary ? (
                  <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 leading-relaxed mb-4">
                    {data.ai_enrichment.county_strategy_summary}
                  </p>
                ) : null}
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <h3 className="text-xs font-black uppercase tracking-wider text-emerald-800 mb-2">FTB Playbook</h3>
                    <ul className="space-y-2 text-sm text-emerald-900 font-semibold">
                      {data.ai_enrichment.first_time_buyer_playbook.map((item) => (
                        <li key={item}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
                    <h3 className="text-xs font-black uppercase tracking-wider text-sky-800 mb-2">Negotiation Moves</h3>
                    <ul className="space-y-2 text-sm text-sky-900 font-semibold">
                      {data.ai_enrichment.negotiation_moves.map((item) => (
                        <li key={item}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                    <h3 className="text-xs font-black uppercase tracking-wider text-rose-800 mb-2">Retrofit Watchouts</h3>
                    <ul className="space-y-2 text-sm text-rose-900 font-semibold">
                      {data.ai_enrichment.retrofit_watchouts.map((item) => (
                        <li key={item}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="rounded-3xl border border-slate-200 bg-white p-6">
              <h2 className="text-xl font-black text-slate-900 mb-3 flex items-center gap-2">
                <BadgeEuro className="h-5 w-5 text-emerald-700" /> Key Summary
              </h2>
              <div className="grid md:grid-cols-4 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">Price Premium</p>
                  <p className="text-2xl font-black text-slate-900">{formatPct(data.calculations.new_build_price_premium_pct)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">Green Rate Discount</p>
                  <p className="text-2xl font-black text-slate-900">{formatRate(data.calculations.green_rate_discount_pct_points)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">Annual Heating Saving</p>
                  <p className="text-2xl font-black text-slate-900">{formatCurrency(data.calculations.annual_heating_saving)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">5-Year New Build Advantage</p>
                  <p className="text-2xl font-black text-slate-900">{formatCurrency(data.calculations.five_year_total_saving_new_build)}</p>
                </div>
              </div>
            </div>

            <PseoEmailCapture
              pageType="new-vs-old"
              pageSlug={data.slug}
              locationName={data.county_name}
              contextHeadline={`Stay Ahead On New Build vs Old Build In ${data.county_name}`}
              contextReason={`Get targeted Irish property updates for buyers comparing new-build and second-hand homes in ${data.county_name}, including grant/rate changes and fresh local comparison reports.`}
            />

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-9 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">FAQ</h2>
              <div className="space-y-3">
                {mergedFaq.map((faq, index) => (
                  <details
                    key={`${faq.question}-${index}`}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <summary className="cursor-pointer font-bold text-slate-800">{faq.question}</summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-amber-200 bg-amber-50 p-5">
              <p className="text-xs text-amber-900 font-semibold leading-relaxed">
                {data.sections.disclaimer}
              </p>
            </div>
          </section>

          <aside className="lg:col-span-4">
            <div className="sticky top-8 space-y-6">
              <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-6 shadow-xl shadow-emerald-900/20">
                <h3 className="text-xl font-black mb-2">Grant + BER Advantage</h3>
                <p className="text-3xl font-black text-emerald-200 mb-2 flex items-center gap-2">
                  <Leaf className="h-6 w-6" /> {formatPct(data.calculations.annual_heating_saving_pct)}
                </p>
                <p className="text-emerald-100/90 text-sm mb-5 leading-relaxed">
                  Estimated annual heating saving from A-rated new build efficiency versus older D-rated stock.
                </p>
                <a
                  href={data.ctas.mortgage_calculator.path}
                  className="no-underline flex items-start gap-2 justify-between rounded-xl bg-white text-emerald-800 px-4 py-3 font-black text-sm hover:bg-emerald-50 transition-all mb-2"
                >
                  <span>{data.ctas.mortgage_calculator.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                </a>
                <a
                  href={data.ctas.retrofit_estimator.path}
                  className="no-underline flex items-start gap-2 justify-between rounded-xl bg-emerald-500 text-white px-4 py-3 font-black text-sm hover:bg-emerald-600 transition-all"
                >
                  <span>{data.ctas.retrofit_estimator.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                </a>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6">
                <h3 className="text-xl font-black text-slate-900 mb-3">Property Actions</h3>
                <a
                  href={data.ctas.rent.path}
                  className="no-underline flex items-start gap-2 justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700 hover:border-emerald-300 hover:text-emerald-700 mb-2"
                >
                  <span>{data.ctas.rent.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                </a>
                <a
                  href={data.ctas.buy.path}
                  className="no-underline flex items-start gap-2 justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700 hover:border-sky-300 hover:text-sky-700"
                >
                  <span>{data.ctas.buy.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                </a>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6">
                <h3 className="text-xl font-black text-slate-900 mb-3">Related Counties</h3>
                <div className="space-y-2">
                  {data.ctas.related.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="no-underline flex items-start gap-2 justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
                    >
                      <span>{item.label}</span>
                      <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50 p-6">
                <h3 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-emerald-600" /> Share RentSmart.ie
                </h3>
                <p className="text-sm text-slate-600 font-medium mb-4 leading-relaxed">
                  Share this county decision audit with first-time buyers comparing new and old stock.
                </p>
                <div className="flex items-center gap-2">
                  <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton url={shareUrl} title={shareText}>
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <WhatsappShareButton url={shareUrl} title={shareText}>
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton url={shareUrl} title="New Build vs Old Build" summary={shareText}>
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6">
                <Link
                  to="/new-vs-old"
                  className="no-underline inline-flex items-center gap-2 text-sm font-black text-emerald-700 hover:text-emerald-800"
                >
                  Try another county <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {data.calculations.five_year_total_cost_new_build <= 0 ? (
          <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 font-semibold">
            <AlertTriangle className="h-4 w-4 inline-block mr-2" />
            Cost estimate is unavailable for this slug. Please return to the hub and choose another county.
          </div>
        ) : null}

        <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-xs text-amber-900 font-semibold leading-relaxed">
            Grants and mortgage rates change over time. Confirm the latest scheme rules and lender rates before any financial commitment.
          </p>
        </section>
      </main>
    </div>
  );
};

export default NewVsOldDetailPage;
